import React, { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  Icon,
  Spacing,
  Typography,
  StyledDiv,
} from '@taleemabad/dsm/components';
import styled from '@emotion/styled';
import { isUrduText } from '@taleemabad/dsm/utils';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';

export interface UGEGSubQuestion {
  text: string;
  marks: number;
  lines: number;
}

export interface UGEGQuestion {
  id: string;
  question?: string;
  marks: number;
  blooms?: string;
  category: 'seen' | 'unseen';
  type: 'objective' | 'subjective';
  formatType: string;
  mainQuestion?: string;
  rawQuestion?: string;
  image?: string;
  passage?: string;
  path?: string;
  options?: string[];
  column_a?: string[];
  column_b?: string[];
  questions?: Array<{ question: string; marks: number; lines: number }>;
  subQuestions?: UGEGSubQuestion[];
  subQuestionSource?: string;
}

interface QuestionGroup {
  formatType: string;
  mainQuestion?: string;
  questions: Array<UGEGQuestion & { globalIndex: number }>;
}

export interface UGEGGeneratedPaperProps {
  questions: UGEGQuestion[];
  selectedQuestionIds: string[];
  onQuestionToggle: (id: string, checked: boolean) => void;
  onSelectAllChange: (checked: boolean) => void;
  onGenerateExamClick: () => void;
  onQuestionEdit?: (
    questionId: string,
    questionText: string,
    marks: number,
    options?: string[],
    subQuestions?: Array<{ text: string; marks: number }>,
    subQuestionSource?: string,
    editPassage?: boolean,
  ) => Promise<void>;
  showBlooms?: boolean;
}

function getEffectiveMarks(
  question: UGEGQuestion & { globalIndex: number },
  selectedQuestionIds: string[],
): number {
  const subQs = question.subQuestions || question.questions || [];
  if (subQs.length === 0) return question.marks;
  return subQs.reduce((sum, sq, i) => {
    const sqId = `${question.id}-sq-${i}`;
    return selectedQuestionIds.includes(sqId) ? sum + sq.marks : sum;
  }, 0);
}

interface QuestionBodyExtrasProps {
  question: UGEGQuestion & { globalIndex: number };
  selectedQuestionIds?: string[];
  onQuestionToggle?: (id: string, checked: boolean) => void;
  hidePassage?: boolean;
}

const QuestionBodyExtras: React.FC<QuestionBodyExtrasProps> = ({
  question,
  selectedQuestionIds,
  onQuestionToggle,
  hidePassage,
}) => {
  const subQuestionsArray = question.subQuestions || question.questions || [];

  return (
    <>
      {question.passage && !hidePassage && (
        <PassageContainer isUrdu={isUrduText(question.passage)}>
          <Typography variant="textBodySmallOnSurfaceVariant">
            {question.passage}
          </Typography>
        </PassageContainer>
      )}
      {question.column_a &&
        question.column_b &&
        question.column_a.length > 0 &&
        (() => {
          const isUrduCol = isUrduText(question.column_a[0] ?? '');
          return (
            <ColumnTable isUrdu={isUrduCol}>
              <thead>
                <tr>
                  <th>{isUrduCol ? 'کالم (الف)' : 'Column A'}</th>
                  <th>{isUrduCol ? 'کالم (ب)' : 'Column B'}</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({
                  length: Math.max(
                    question.column_a.length,
                    question.column_b.length,
                  ),
                }).map((_, i) => (
                  <tr
                    key={`${question.column_a?.[i] ?? ''}-${question.column_b?.[i] ?? ''}`}
                  >
                    <td>{question.column_a?.[i] ?? ''}</td>
                    <td>{question.column_b?.[i] ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </ColumnTable>
          );
        })()}
      {question.options && question.options.length > 0 && (
        <SubQuestionList>
          {question.options.map((option, i) => {
            const optId = `${question.id}-opt-${i}`;
            return (
              <SubQuestionItem key={optId} isUrdu={isUrduText(option)}>
                <Checkbox
                  checked={selectedQuestionIds?.includes(optId) ?? false}
                  onChange={() =>
                    onQuestionToggle?.(
                      optId,
                      !(selectedQuestionIds?.includes(optId) ?? false),
                    )
                  }
                />
                <Typography variant="textBodySmallOnSurfaceVariant">
                  {option}
                </Typography>
              </SubQuestionItem>
            );
          })}
        </SubQuestionList>
      )}
      {subQuestionsArray.length > 0 && (
        <SubQuestionList>
          {subQuestionsArray.map((sq, i) => {
            const sqText = 'text' in sq ? sq.text : sq.question;
            return (
              <SubQuestionItem
                key={`${question.id}-sq-${i}`}
                isUrdu={isUrduText(sqText)}
              >
                <Checkbox
                  checked={
                    selectedQuestionIds?.includes(`${question.id}-sq-${i}`) ??
                    false
                  }
                  onChange={() =>
                    onQuestionToggle?.(
                      `${question.id}-sq-${i}`,
                      !(
                        selectedQuestionIds?.includes(
                          `${question.id}-sq-${i}`,
                        ) ?? false
                      ),
                    )
                  }
                />
                <Typography variant="textBodySmallOnSurfaceVariant">
                  {i + 1}. {sqText}
                </Typography>
                <Typography variant="textBodySmallPrimary40">
                  ({sq.marks})
                </Typography>
              </SubQuestionItem>
            );
          })}
        </SubQuestionList>
      )}
    </>
  );
};

interface QuestionEditorProps {
  question: UGEGQuestion & { globalIndex: number };
  groupMainQuestion?: string;
  onSave: (
    questionId: string,
    text: string,
    marks: number,
    options?: string[],
    subQuestions?: Array<{ text: string; marks: number }>,
    subQuestionSource?: string,
    editPassage?: boolean,
  ) => Promise<void>;
  showBlooms?: boolean;
  selectedQuestionIds?: string[];
  onQuestionToggle?: (id: string, checked: boolean) => void;
}

const QuestionEditor: React.FC<QuestionEditorProps> = ({
  question,
  groupMainQuestion,
  onSave,
  showBlooms,
  selectedQuestionIds,
  onQuestionToggle,
}) => {
  const isPassageQuestion =
    !!question.passage &&
    (!question.question ||
      question.question.trim() === (groupMainQuestion ?? '').trim());
  const getInitialText = () =>
    isPassageQuestion
      ? (question.passage ?? '')
      : (question.rawQuestion ??
        question.question ??
        question.mainQuestion ??
        '');

  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(getInitialText);
  const [editMarks, setEditMarks] = useState(question.marks);
  const [isSaving, setIsSaving] = useState(false);
  const subQuestionsArray = question.subQuestions || question.questions || [];
  const [editOptions, setEditOptions] = useState<string[] | undefined>(
    question.options ? [...question.options] : undefined,
  );
  const [editSubQuestions, setEditSubQuestions] = useState<
    Array<{ text: string; marks: number }>
  >(
    subQuestionsArray.map((sq) => ({
      text: 'text' in sq ? sq.text : sq.question,
      marks: sq.marks,
    })),
  );

  const handleEdit = () => {
    setEditText(getInitialText());
    setEditMarks(question.marks);
    setEditOptions(question.options ? [...question.options] : undefined);
    setEditSubQuestions(
      subQuestionsArray.map((sq) => ({
        text: 'text' in sq ? sq.text : sq.question,
        marks: sq.marks,
      })),
    );
    setIsEditing(true);
  };

  const handleCancel = () => setIsEditing(false);

  const handleSave = async () => {
    const trimmedText = (editText || '').trim();
    if (!trimmedText) return;
    const safeMarks = Math.max(0, Number(editMarks) || 0);
    setIsSaving(true);
    try {
      await onSave(
        question.id,
        trimmedText,
        safeMarks,
        editOptions,
        editSubQuestions.length > 0 ? editSubQuestions : undefined,
        question.subQuestionSource,
        isPassageQuestion,
      );
      setIsEditing(false);
    } catch {
      // Keep editor open so the teacher can retry
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <EditContainer>
        <EditTextArea
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          rows={4}
          aria-label="Edit question text"
          disabled={isSaving}
        />
        {editOptions !== undefined && (
          <EditSubSection>
            <Typography variant="textBodySmallOnSurfaceVariant">
              Options:
            </Typography>
            {editOptions.map((opt, i) => (
              <EditTextArea
                key={`opt-${opt}-${i}`}
                value={opt}
                onChange={(e) => {
                  const updated = [...editOptions];
                  updated[i] = e.target.value;
                  setEditOptions(updated);
                }}
                rows={1}
                aria-label={`Edit option ${i + 1}`}
                disabled={isSaving}
              />
            ))}
          </EditSubSection>
        )}
        {editSubQuestions.length > 0 && (
          <EditSubSection>
            <Typography variant="textBodySmallOnSurfaceVariant">
              Sub-questions:
            </Typography>
            {editSubQuestions.map((sq, i) => (
              <SubQuestionEditorRow key={`sq-${sq.text}-${i}`}>
                <EditTextArea
                  value={sq.text}
                  onChange={(e) => {
                    const updated = editSubQuestions.map((s, j) =>
                      j === i ? { ...s, text: e.target.value } : s,
                    );
                    setEditSubQuestions(updated);
                  }}
                  rows={2}
                  aria-label={`Edit sub-question ${i + 1}`}
                  disabled={isSaving}
                />
                <EditMarksInput
                  type="number"
                  min={0}
                  value={sq.marks}
                  onChange={(e) => {
                    const updated = editSubQuestions.map((s, j) =>
                      j === i ? { ...s, marks: Number(e.target.value) } : s,
                    );
                    setEditSubQuestions(updated);
                  }}
                  aria-label={`Edit sub-question marks ${i + 1}`}
                  disabled={isSaving}
                />
              </SubQuestionEditorRow>
            ))}
          </EditSubSection>
        )}
        <EditFooter>
          <MarksRow>
            <Typography variant="textBodySmallOnSurfaceVariant">
              Marks:
            </Typography>
            <EditMarksInput
              type="number"
              min={0}
              value={editMarks}
              onChange={(e) => setEditMarks(Number(e.target.value))}
              aria-label="Edit marks"
              disabled={isSaving}
            />
          </MarksRow>
          <EditActions>
            <Button variant="text" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant="filled"
              onClick={handleSave}
              loading={isSaving}
              disabled={isSaving || !(editText || '').trim()}
            >
              Save
            </Button>
          </EditActions>
        </EditFooter>
      </EditContainer>
    );
  }

  const questionText = question.question || '';
  const isPassageOnly =
    !!question.passage &&
    (!questionText || questionText.trim() === (groupMainQuestion ?? '').trim());
  const questionIsUrdu = isUrduText(
    (questionText || question.passage || '').split('\n')[0],
  );

  return (
    <QuestionContentWrapper>
      <QuestionHeaderRow isUrdu={questionIsUrdu}>
        <QuestionTextRow isUrdu={questionIsUrdu}>
          <Typography variant="textBodyMediumSurface">
            {question.globalIndex}.
          </Typography>
          {isPassageOnly ? (
            <InlinePassage isUrdu={questionIsUrdu}>
              <Typography variant="textBodySmallOnSurfaceVariant">
                {question.passage}
              </Typography>
            </InlinePassage>
          ) : (
            <>
              <Typography variant="textBodyMediumSurface">
                {` ${questionText.split('\n')[0] || question.mainQuestion || ''}`}
              </Typography>
              {showBlooms && question.blooms && (
                <Badge size="small" label={question.blooms} type="outlined" />
              )}
            </>
          )}
        </QuestionTextRow>
        <InlineRow>
          <Typography variant="textBodySmallPrimary40">
            ({getEffectiveMarks(question, selectedQuestionIds ?? [])})
          </Typography>
          <EditIconButton
            onClick={handleEdit}
            title="Edit question"
            aria-label="Edit question"
          >
            <Icon
              name="edit_note"
              size="regular"
              color="--md-ref-primary-50"
              cursor="pointer"
            />
          </EditIconButton>
        </InlineRow>
      </QuestionHeaderRow>
      {question.image && (
        <img
          src={question.image}
          alt="Question illustration"
          style={{ maxWidth: '100%', marginTop: 'var(--rk-spacing-sm)' }}
        />
      )}
      <QuestionBodyExtras
        question={question}
        hidePassage={isPassageOnly || isPassageQuestion}
        selectedQuestionIds={selectedQuestionIds}
        onQuestionToggle={onQuestionToggle}
      />
    </QuestionContentWrapper>
  );
};

// ---------- Main component ----------

export const UGEGGeneratedPaper: React.FC<UGEGGeneratedPaperProps> = ({
  questions,
  selectedQuestionIds,
  onQuestionToggle,
  onSelectAllChange,
  onGenerateExamClick,
  onQuestionEdit,
  showBlooms,
}) => {
  const totalMarks = questions
    .filter((q) => selectedQuestionIds.includes(q.id))
    .reduce((sum, q) => {
      const subQs = q.subQuestions || q.questions || [];
      if (subQs.length === 0) return sum + q.marks;
      const selectedSubMarks = subQs.reduce((s, sq, i) => {
        const sqId = `${q.id}-sq-${i}`;
        return selectedQuestionIds.includes(sqId) ? s + sq.marks : s;
      }, 0);
      return sum + selectedSubMarks;
    }, 0);

  const totalSelectableCount = useMemo(
    () =>
      questions.reduce((sum, q) => {
        const subQs = q.subQuestions || q.questions || [];
        const opts = q.options || [];
        return sum + 1 + subQs.length + opts.length;
      }, 0),
    [questions],
  );
  const allSelected =
    totalSelectableCount > 0 &&
    questions.every((q) => selectedQuestionIds.includes(q.id));

  const questionGroups = useMemo<QuestionGroup[]>(() => {
    const groups: QuestionGroup[] = [];
    let counter = 0;
    for (const question of questions) {
      counter++;
      const lastGroup = groups[groups.length - 1];
      const sameGroup =
        lastGroup &&
        lastGroup.formatType === question.formatType &&
        lastGroup.mainQuestion === question.mainQuestion;
      if (sameGroup) {
        lastGroup.questions.push({ ...question, globalIndex: counter });
      } else {
        groups.push({
          formatType: question.formatType,
          mainQuestion: question.mainQuestion,
          questions: [{ ...question, globalIndex: counter }],
        });
      }
    }
    return groups;
  }, [questions]);

  return (
    <StyledDiv flexDirection="column">
      <StickyHeader>
        <Spacing marginBottom="lg">
          <Typography variant="textTitleMediumSurfaceVariant">
            Generated Paper
          </Typography>
        </Spacing>
        <StyledDiv justifyContent="space-between" alignItem="center">
          <Checkbox
            key={`select-all-${allSelected}`}
            label="Select All Questions"
            checked={allSelected}
            onChange={() => onSelectAllChange(!allSelected)}
            data-cy="ug-eg-select-all-questions"
          />
          <Typography variant="textBodyMediumPrimary">
            Total Marks: {totalMarks}
          </Typography>
        </StyledDiv>
      </StickyHeader>

      <Spacing margin="lg none" paddingBottom="xxxxxxl">
        <StyledDiv flexDirection="column" gap={8}>
          {questionGroups.map((group) => (
            <StyledDiv
              key={`group-${group.questions[0].id}`}
              flexDirection="column"
            >
              {group.mainQuestion &&
                (group.questions.some((q) => q.passage) ||
                  group.questions.some(
                    (q) => q.question && q.question !== group.mainQuestion,
                  )) && (
                  <GroupHeading>
                    <Typography variant="textBodyMediumSurface">
                      {group.mainQuestion}
                    </Typography>
                  </GroupHeading>
                )}
              {group.questions.map((question) => {
                const cardIsUrdu = isUrduText(
                  (question.question || question.passage || '').split('\n')[0],
                );
                return (
                  <QuestionCard key={question.id} isUrdu={cardIsUrdu}>
                    <CheckboxQuestionWrapper>
                      <Checkbox
                        key={`${question.id}-${selectedQuestionIds.includes(question.id)}`}
                        checked={selectedQuestionIds.includes(question.id)}
                        onChange={() =>
                          onQuestionToggle(
                            question.id,
                            !selectedQuestionIds.includes(question.id),
                          )
                        }
                        data-cy={`ug-eg-question-checkbox-${question.id}`}
                      />
                      {onQuestionEdit ? (
                        <QuestionEditor
                          question={question}
                          groupMainQuestion={group.mainQuestion}
                          onSave={onQuestionEdit}
                          showBlooms={showBlooms}
                          selectedQuestionIds={selectedQuestionIds}
                          onQuestionToggle={onQuestionToggle}
                        />
                      ) : (
                        <StyledDiv flexDirection="column" gap={4}>
                          {(() => {
                            const questionText = question.question || '';
                            const passageOnly =
                              !!question.passage &&
                              (!questionText ||
                                questionText.trim() ===
                                (group.mainQuestion ?? '').trim());
                            const questionIsUrdu = isUrduText(
                              (questionText || question.passage || '').split(
                                '\n',
                              )[0],
                            );
                            return (
                              <>
                                <QuestionHeaderRow isUrdu={questionIsUrdu}>
                                  <QuestionTextRow isUrdu={questionIsUrdu}>
                                    <Typography variant="textBodyMediumSurface">
                                      {question.globalIndex}.
                                    </Typography>
                                    {passageOnly ? (
                                      <InlinePassage isUrdu={questionIsUrdu}>
                                        <Typography variant="textBodySmallOnSurfaceVariant">
                                          {question.passage}
                                        </Typography>
                                      </InlinePassage>
                                    ) : (
                                      <>
                                        <Typography variant="textBodyMediumSurface">
                                          {` ${questionText.split('\n')[0] || question.mainQuestion || ''}`}
                                        </Typography>
                                        {showBlooms && question.blooms && (
                                          <Badge
                                            size="small"
                                            label={question.blooms}
                                            type="outlined"
                                          />
                                        )}
                                      </>
                                    )}
                                  </QuestionTextRow>
                                  <Typography variant="textBodySmallPrimary40">
                                    (
                                    {getEffectiveMarks(
                                      question,
                                      selectedQuestionIds,
                                    )}
                                    )
                                  </Typography>
                                </QuestionHeaderRow>
                                {question.image && (
                                  <img
                                    src={question.image}
                                    alt="Question illustration"
                                    style={{
                                      maxWidth: '100%',
                                      marginTop: 'var(--rk-spacing-sm)',
                                    }}
                                  />
                                )}
                                <QuestionBodyExtras
                                  question={question}
                                  selectedQuestionIds={selectedQuestionIds}
                                  onQuestionToggle={onQuestionToggle}
                                  hidePassage={passageOnly}
                                />
                              </>
                            );
                          })()}
                        </StyledDiv>
                      )}
                    </CheckboxQuestionWrapper>
                  </QuestionCard>
                );
              })}
            </StyledDiv>
          ))}
        </StyledDiv>
      </Spacing>

      <SubmitBtn>
        <Button
          data-cy="ug-eg-generate-exam-button"
          onClick={onGenerateExamClick}
          disabled={selectedQuestionIds.length === 0}
        >
          Generate Exam
        </Button>
      </SubmitBtn>
    </StyledDiv>
  );
};

const StickyHeader = styled.div`
  padding: var(--rk-spacing-lg) var(--rk-spacing-sm);
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--md-sys-color-on-primary);
`;

const GroupHeading = styled.div`
  padding: var(--rk-spacing-lg) var(--rk-spacing-md) var(--rk-spacing-sm);
`;

const QuestionCard = styled.div<{ isUrdu?: boolean }>`
  padding: var(--rk-spacing-md);
  border-bottom: 1px solid var(--md-sys-color-outline-variant);
  direction: ${(props) => (props.isUrdu ? 'rtl' : 'ltr')};
`;

const QuestionHeaderRow = styled.div<{ isUrdu: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  direction: ${(props) => (props.isUrdu ? 'rtl' : 'ltr')};
  text-align: ${(props) => (props.isUrdu ? 'right' : 'left')};
  font-family: ${(props) =>
    props.isUrdu
      ? "'Gulzar', 'Noto Nastaliq Urdu', serif !important"
      : 'inherit'};

  ${(props) =>
    props.isUrdu
      ? `
    & > * {
      font-family: 'Gulzar', 'Noto Nastaliq Urdu', serif !important;
    }
  `
      : ''}
`;

const SubQuestionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--rk-spacing-xs);
  margin-top: var(--rk-spacing-sm);
  padding-left: var(--rk-spacing-lg);
`;

const SubQuestionItem = styled.div<{ isUrdu?: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: var(--rk-spacing-sm);
  direction: ${(props) => (props.isUrdu ? 'rtl' : 'ltr')};
  text-align: ${(props) => (props.isUrdu ? 'right' : 'left')};
  font-family: ${(props) =>
    props.isUrdu
      ? "'Gulzar', 'Noto Nastaliq Urdu', serif !important"
      : 'inherit'};
  flex-wrap: wrap;

  ${(props) =>
    props.isUrdu
      ? `
    & > * {
      font-family: 'Gulzar', 'Noto Nastaliq Urdu', serif !important;
    }
  `
      : ''}
`;

const SubmitBtn = styled.div`
  display: flex;
  gap: var(--rk-spacing-xl);
  justify-content: left;
  margin-right: var(--rk-spacing-xl);
  width: -webkit-fill-available;
  padding: var(--rk-spacing-xxl) 0;
  position: fixed;
  background-color: var(--md-ref-neutral-100);
  bottom: 0;

  ${mq(BreakpointSizes.xs)} {
    justify-content: center;
    md-filled-button {
      flex: 1;
    }
  }
`;

// --- Inline editor styled components ---

const QuestionContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--rk-spacing-xs);
  flex: 1;
`;

const InlineRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--rk-spacing-xs);
  flex-shrink: 0;
`;

const QuestionTextRow = styled.div<{ isUrdu?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: var(--rk-spacing-sm);
  flex-wrap: wrap;
  direction: ${(props) => (props.isUrdu ? 'rtl' : 'ltr')};
  text-align: ${(props) => (props.isUrdu ? 'right' : 'left')};
  font-family: ${(props) =>
    props.isUrdu
      ? "'Gulzar', 'Noto Nastaliq Urdu', serif !important"
      : 'inherit'};

  ${(props) =>
    props.isUrdu
      ? `
    & > * {
      font-family: 'Gulzar', 'Noto Nastaliq Urdu', serif !important;
    }
  `
      : ''}
`;

const EditIconButton = styled.button`
  background: none;
  border: none;
  padding: var(--rk-spacing-xs);
  cursor: pointer;
  flex-shrink: 0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  &:hover {
    background-color: var(--md-ref-primary-95);
  }
`;

const EditContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--rk-spacing-sm);
  padding: var(--rk-spacing-sm);
  border: 1.5px solid var(--md-ref-primary-80);
  border-radius: 8px;
  background-color: var(--md-ref-primary-99);
  flex: 1;
`;

const EditTextArea = styled.textarea`
  width: 100%;
  padding: var(--rk-spacing-sm);
  border: 1.5px solid var(--md-ref-primary-70);
  border-radius: 6px;
  font-family: Arial, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  min-height: 80px;
  outline: none;
  box-sizing: border-box;
  &:focus {
    border-color: var(--md-ref-primary-50);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EditFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--rk-spacing-sm);
  flex-wrap: wrap;
`;

const MarksRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--rk-spacing-sm);
`;

const EditMarksInput = styled.input`
  width: 64px;
  padding: 6px var(--rk-spacing-sm);
  border: 1.5px solid var(--md-ref-primary-70);
  border-radius: 6px;
  font-family: Arial, sans-serif;
  font-size: 14px;
  text-align: center;
  outline: none;
  &:focus {
    border-color: var(--md-ref-primary-50);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EditSubSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--rk-spacing-xs);
  padding-top: var(--rk-spacing-xs);
  border-top: 1px solid var(--md-ref-primary-90);
`;

const SubQuestionEditorRow = styled.div`
  display: flex;
  gap: var(--rk-spacing-sm);
  align-items: flex-start;
`;

const EditActions = styled.div`
  display: flex;
  gap: var(--rk-spacing-sm);
  margin-left: auto;
`;

const InlinePassage = styled.div<{ isUrdu?: boolean }>`
  flex: 1;
  padding: var(--rk-spacing-sm) var(--rk-spacing-md);
  background-color: var(--md-ref-neutral-95);
  border-left: 4px solid var(--md-ref-primary-40);
  border-radius: 4px;
  font-style: italic;
  direction: ${(props) => (props.isUrdu ? 'rtl' : 'ltr')};
  text-align: ${(props) => (props.isUrdu ? 'right' : 'left')};
  font-family: ${(props) =>
    props.isUrdu
      ? "'Gulzar', 'Noto Nastaliq Urdu', serif !important"
      : 'inherit'};
`;

const ColumnTable = styled.table<{ isUrdu?: boolean }>`
  width: 100%;
  border-collapse: collapse;
  margin: var(--rk-spacing-sm) 0;
  direction: ${({ isUrdu }) => (isUrdu ? 'rtl' : 'ltr')};
  font-family: ${({ isUrdu }) =>
    isUrdu ? "'Gulzar', 'Noto Nastaliq Urdu', serif" : 'inherit'};
  thead th {
    padding: var(--rk-spacing-sm) var(--rk-spacing-md);
    background-color: var(--md-ref-secondary-95);
    border: 1px solid var(--md-sys-color-outline-variant);
    font-weight: 600;
    width: 50%;
    text-align: ${({ isUrdu }) => (isUrdu ? 'right' : 'left')};
  }
  tbody td {
    padding: var(--rk-spacing-sm) var(--rk-spacing-md);
    border: 1px solid var(--md-sys-color-outline-variant);
    width: 50%;
    text-align: ${({ isUrdu }) => (isUrdu ? 'right' : 'left')};
  }
`;

const PassageContainer = styled.div<{ isUrdu?: boolean }>`
  padding: var(--rk-spacing-md);
  margin-bottom: var(--rk-spacing-md);
  background-color: var(--md-ref-neutral-95);
  border-left: 4px solid var(--md-ref-primary-40);
  border-radius: 4px;
  direction: ${(props) => (props.isUrdu ? 'rtl' : 'ltr')};
  text-align: ${(props) => (props.isUrdu ? 'right' : 'left')};
  font-family: ${(props) =>
    props.isUrdu
      ? "'Gulzar', 'Noto Nastaliq Urdu', serif !important"
      : 'inherit'};

  & p {
    font-style: italic;
    margin: 0;
  }
`;

const CheckboxQuestionWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  & > *:last-child {
    flex: 1;
    min-width: 0;
  }
`;
